import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { useModelPermission } from '../../hooks/useModelPermission';
import { axiosInstance } from '../../services/axiosConfig';

interface RegistrationConfig {
    nivel_usuario: number | null;
    configuracion_valida: boolean;
    niveles_disponibles: { id: number; nombre: string }[];
}

export default function ConfigRegistroPage() {
    const { user } = useAuth();
    const { isDark } = useTheme();
    const permission = useModelPermission('registroclienteconfig', user?.user_level_id || 0);
    const [config, setConfig] = useState<RegistrationConfig | null>(null);
    const [selected, setSelected] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    useEffect(() => {
        let active = true;
        axiosInstance.get<RegistrationConfig>('configuracion-registro/')
            .then(({ data }) => {
                if (!active) return;
                setConfig(data);
                setSelected(data.configuracion_valida ? String(data.nivel_usuario) : '');
            })
            .catch(() => { if (active) setError('No se pudo cargar la configuración. Comprueba tus permisos y vuelve a intentar.'); })
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, []);

    const save = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!selected || !permission?.can_update || saving) return;
        setSaving(true);
        setError('');
        setSuccess('');
        try {
            const { data } = await axiosInstance.put<RegistrationConfig>('configuracion-registro/', { nivel_usuario: Number(selected) });
            setConfig(data);
            setSelected(data.configuracion_valida ? String(data.nivel_usuario) : '');
            setSuccess('Configuración guardada. Los próximos registros usarán este nivel.');
        } catch (err: any) {
            const data = err.response?.data;
            setError(data?.nivel_usuario?.[0] || data?.detail || 'No se pudo guardar la configuración.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <main data-admin-page="config-registro" style={{ padding: '24px', maxWidth: 850, margin: '0 auto', color: isDark ? '#f8fafc' : '#1e293b' }}>
            <h1>Registro de clientes</h1>
            <p>Elige el nivel que se asignará automáticamente a las nuevas cuentas creadas desde /register.</p>
            <p>Los clientes existentes conservarán su nivel actual.</p>
            {error && <p role="alert" style={{ color: isDark ? '#fca5a5' : '#b91c1c' }}>{error}</p>}
            {success && <p role="status" style={{ color: isDark ? '#86efac' : '#166534' }}>{success}</p>}
            {loading ? <p role="status">Cargando configuración…</p> : config && (
                <form onSubmit={save} style={{ padding: 24, borderRadius: 16, background: isDark ? '#251b35' : '#fff', border: '1px solid #94a3b8' }}>
                    {!config.configuracion_valida && <p role="alert">Selecciona y guarda un nivel activo para habilitar el registro de clientes.</p>}
                    <label htmlFor="registration-level">Nivel inicial del cliente</label>
                    <select id="registration-level" value={selected} onChange={event => { setSelected(event.target.value); setSuccess(''); }} disabled={saving || !permission?.can_update} style={{ display: 'block', width: '100%', padding: 12, margin: '12px 0', color: isDark ? '#f8fafc' : '#1e293b', background: isDark ? '#251b35' : '#fff' }}>
                        <option value="">Selecciona un nivel</option>
                        {config.niveles_disponibles.map(level => <option key={level.id} value={level.id}>{level.nombre}</option>)}
                    </select>
                    <p>Solo se muestran niveles activos con perfil de cliente. Selecciona CLIENTES si ese es el nivel que deseas asignar.</p>
                    {!config.niveles_disponibles.length && <p role="alert">Crea o activa un nivel con perfil de cliente en Permisos antes de continuar.</p>}
                    {permission && !permission.can_update && <p>Tienes acceso de lectura. Solicita permiso de actualización para cambiar esta opción.</p>}
                    <button type="submit" disabled={!selected || saving || !permission?.can_update} style={{ padding: '12px 20px', borderRadius: 10, border: 0, background: '#61257B', color: '#fff' }}>{saving ? 'Guardando…' : 'Guardar configuración'}</button>
                </form>
            )}
        </main>
    );
}
